import { ConnectionType } from '@accedo/ctv-device-api';
import { logger } from '@accedo/logger';
import {
  ParsedResponse,
  promiseFetch,
  PromiseFetchSettings,
} from '@accedo/promise-fetch';
import { sleep } from '@accedo/general-utils';
import {
  getOzTamDeviceId,
  getOztamLiveMediaId,
  getVendorVersion,
  roundPosition,
} from '##/services/analytics/OzTam/utils';
import { config } from '##/services/store/store';
import generateUUID from '##/utils/generateUUID';
import { getIpAddress, isDeviceIQ, isDeviceMagneto } from '##/utils/device';
import { sendErrorToTealium } from '##/components/Error';
import { Messengers } from '##/components/Player/utils/Messengers';
import { BVODRequestParams, BVODResponse } from '##/services/tenApi/dai/types';
import { isLoggedIn } from '##/services/authentication/Authentication';
import { PageNavigation } from '##/utils/PageNavigation';
import { StandardCarouselItem } from '##/components/StandardCarousel/types';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';
import { getOS, BVOD_N10_TOKEN_KEY } from '##/utils';
import {
  TealiumErrorCode,
  TealiumErrorContext,
} from '../Tealium/TealiumErrorTypes';
import { Event, EventType, MeterEvent, VideoData } from './types';
import { OZTAM_VOD_PUBLISHER_ID } from './OzTamBridge';

const PublisherName = 'TEN';

const isBoxDevice = (): boolean => isDeviceIQ() || isDeviceMagneto();

const { url: ozTamUrl } = config.$get().ozTam;
const SESSION_UPDATER_TIMER = 10 * 60 * 1000;

const inValidRange = (value: number, maxNum = 30): number => {
  const safeValue = Math.max(0, value);

  return safeValue <= maxNum ? safeValue : 0;
};

const BVOD = {
  SIGN_JWT: '/sign_jwt',
  AUTH: '/auth',
  NO_AUTH: '/get_demo/noauth',
};

const N10_UNIQUE_BVOD_API_KEY = '///adjust.bouncing.flame';

export class OzTamClient {
  private timezoneOffset: number = Math.abs(new Date().getTimezoneOffset());

  private lastBeginPosition = 0;
  private sessionId: string = generateUUID();
  private justRegeneratedSessionId = false;
  private lastStartPosition = 0;
  private isPaused = false;
  private videoData: VideoData | null = null;
  private ipAddress = '';
  private deviceId = '';
  private vendorVersion = '';
  private connectionType: ConnectionType | '' = ''; // Leaving blank as advised by Oztam as there's no viable way of getting this value on HbbTV
  private eventDispatcherHandler: number | null = null;
  private sessionRefreshHandler: number | null = null;

  private isLive = false;
  private ptsEpochDifference = 0;
  private lastLiveToPosition = 0;
  private timeFromLastLivePosition = 0;
  private stopRecordingEvents = false;
  private resetTimestamp = 0;
  private timeFromLastReset = 0;

  init = async (): Promise<void> => {
    const ipBlock = await getIpAddress();

    this.ipAddress = ipBlock.ip;
    this.deviceId = await getOzTamDeviceId();
    this.vendorVersion = await getVendorVersion();
    this.sessionId = generateUUID();
    Messengers.ozTamSessionId$.send(this.sessionId);
  };

  onLivePlayerRouteExit = (): void => {
    this.lastLiveToPosition = 0;
    this.timeFromLastLivePosition = 0;
    this.resetTimestamp = 0;
    this.timeFromLastReset = 0;
  };

  initVideo = (video: VideoData, isLive: boolean): void => {
    this.isLive = isLive;

    if (!this.isLive) {
      this.onLivePlayerRouteExit();
    }

    this.videoData = video;
    this.clearTimers();
  };

  clearTimers = (): void => {
    this.clearEventDispatcher();
    this.clearSessionUpdater();
  };

  resetLastBeginPosition = (): void => {
    if (this.lastBeginPosition) {
      this.lastBeginPosition = 0;
    }
  };

  reset = (): void => {
    logger.info('[OzTamClient]: reset() called here');

    this.ptsEpochDifference = 0;
    this.resetLastBeginPosition();
    this.lastStartPosition = 0;
    if (this.isLive) {
      this.resetTimestamp = Math.floor(Date.now() / 1000);
    }
    this.justRegeneratedSessionId = false;
    Messengers.setBVODParams$.resetToDefault();
  };

  clearEventDispatcher = (): void => {
    if (this.eventDispatcherHandler) {
      window.clearInterval(this.eventDispatcherHandler);
      this.eventDispatcherHandler = null;
    }
  };

  setPtsEpochDiff = (diff: number, resetPtsEpoch = false): void => {
    const newPts = Math.floor(diff);
    const currentPts = Math.floor(this.ptsEpochDifference);

    if (newPts <= currentPts && !resetPtsEpoch) {
      return;
    }

    this.ptsEpochDifference = diff;
  };

  getPtsEpochDiff = (): number => this.ptsEpochDifference;

  refreshOzTamSession = (onRestartStream = false): void => {
    if (this.isPaused || onRestartStream) {
      logger.info('[OzTamClient] refresh OzTam Session');
      this.sessionId = generateUUID();
      Messengers.ozTamSessionId$.send(this.sessionId);
      this.justRegeneratedSessionId = true;

      if (onRestartStream) {
        this.clearSessionUpdater();
      }
    }
  };

  setupSessionUpdater = (): void => {
    this.clearSessionUpdater();
    this.sessionRefreshHandler = window.setTimeout(
      this.refreshOzTamSession,
      SESSION_UPDATER_TIMER,
    );
  };

  clearSessionUpdater = (): void => {
    if (this.sessionRefreshHandler) {
      clearTimeout(this.sessionRefreshHandler);
      this.sessionRefreshHandler = null;
    }
  };

  loadEvent = (position = 0): Promise<void> =>
    this.generateEvent(EventType.LOAD, position, position);

  beginEvent = (timeAtBeginning: number): Promise<void> => {
    if (this.isLive) {
      this.lastBeginPosition = timeAtBeginning;
    }

    this.lastStartPosition = timeAtBeginning;
    return this.generateEvent(
      EventType.BEGIN,
      timeAtBeginning,
      timeAtBeginning,
    );
  };

  completeEvent = (position: number): void => {
    this.isPaused = false;

    this.clearSessionUpdater();
    this.generateEvent(EventType.COMPLETE, this.lastStartPosition, position);

    this.lastStartPosition = 0;
  };

  setLastPosition = (position: number): void => {
    this.lastStartPosition = position;
  };

  progressEvent = (position: number, firstAd = false): Promise<void> => {
    if (Messengers.currentAd$.getCurrentValue() && !firstAd) {
      return Promise.resolve();
    }

    if (this.isPaused) {
      this.isPaused = false;
      this.clearSessionUpdater();
    }

    const lastStartTime = this.lastBeginPosition || this.lastStartPosition;

    if (position !== lastStartTime) {
      this.resetLastBeginPosition();

      this.lastStartPosition = position;

      return this.generateEvent(EventType.PROGRESS, lastStartTime, position);
    }

    return Promise.resolve();
  };

  pauseEvent = (): void => {
    this.isPaused = true;
    this.setupSessionUpdater();
  };

  wasSessionJustRegenerated = (): boolean => this.justRegeneratedSessionId;

  playingEvent = (position: number): void => {
    this.isPaused = false;
    // first playing event after regenerate -> send load & begin
    if (this.justRegeneratedSessionId) {
      this.justRegeneratedSessionId = false;
      const pos = roundPosition(position);
      this.loadEvent(pos);
      this.beginEvent(pos);
    }
  };

  adBeginEvent = (): Promise<void> =>
    this.generateEvent(
      EventType.AD_BEGIN,
      this.lastStartPosition,
      this.lastStartPosition,
    );

  adCompleteEvent = (): Promise<void> =>
    this.generateEvent(
      EventType.AD_COMPLETE,
      this.lastStartPosition,
      this.lastStartPosition,
    );

  setStopRecordingEvents = (stop: boolean): void => {
    this.stopRecordingEvents = stop;
  };

  getLastPosition = (): number => this.lastStartPosition;

  sendVozStreamingRequest = async (isLiveSession: boolean): Promise<void> => {
    const { OzTamId } = config.$get().user || {};

    if (!isLoggedIn() || !OzTamId) {
      return;
    }

    const currentState = PageNavigation.getCurrentState();

    const { publisherId, episodeCrid, referenceId, altId, liveShow, genre } =
      (currentState as StandardCarouselItem & LiveCarouselItem) || {};

    const MediaId = isLiveSession
      ? getOztamLiveMediaId(
          PageNavigation.getCurrentState() as LiveCarouselItem,
        )
      : episodeCrid || referenceId || altId;

    const body = {
      MediaId,
      PublisherName,
      PublisherId: isLiveSession ? publisherId : OZTAM_VOD_PUBLISHER_ID,
      SessionId: this.sessionId,
      RegistrationId: OzTamId,
      IpAddress: this.ipAddress,
      DeviceId: this.deviceId,
      MediaType: isLiveSession ? 'live' : 'vod',
      DeviceType: isBoxDevice() ? 'box' : 'tv',
      UserAgent: navigator.userAgent,
      Genre: (isLiveSession ? liveShow?.genre : genre) || '',
      TimeStamp: new Date().toISOString(),
      Logging: 'string',
    };

    let bvodResponse: BVODResponse[] | null;

    bvodResponse = await this.makeVozStreamingRequest(body);

    if (!bvodResponse?.length) {
      logger.error(
        '[OzTamClient] Voz streaming request failed, retrying in 20ms....',
      );

      await sleep(20);

      bvodResponse = await this.makeVozStreamingRequest(body);
    }

    if (bvodResponse?.length) {
      Messengers.setBVODParams$.send(bvodResponse);
    }
  };

  private makeSpecificVozRequest = async (
    requestType: keyof typeof BVOD,
    requestBody: BVODRequestParams,
    accessToken?: string,
  ): Promise<BVODResponse[] | { access_token: string }> => {
    const { bvodRequestUrl } = config.$get().api;
    const { data } = await promiseFetch(
      `${bvodRequestUrl}${BVOD[requestType]}`,
      {
        method: 'POST',
        responseType: 'json',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json;charset=UTF-8',
          'publisher-id': requestBody.PublisherId,
          ...(requestType === 'AUTH' && {
            Authorization: `Bearer ${accessToken}`,
          }),
          ...(requestType === 'SIGN_JWT' && {
            'api-key': N10_UNIQUE_BVOD_API_KEY,
          }),
        },
        data: JSON.stringify(requestBody),
      },
    );
    return data;
  };

  private makeVozStreamingRequest = async (
    requestBody: BVODRequestParams,
  ): Promise<BVODResponse[]> => {
    try {
      let accessToken = localStorage.getItem(BVOD_N10_TOKEN_KEY);

      if (!accessToken) {
        const { access_token: newAccessToken } =
          ((await this.makeSpecificVozRequest('SIGN_JWT', requestBody)) as {
            access_token: string;
          }) || {};

        accessToken = newAccessToken;

        localStorage.setItem(BVOD_N10_TOKEN_KEY, newAccessToken);
      } else {
        logger.debug(
          '[OzTamClient] skip fetching BVOD access token as access token saved...',
        );
      }

      const bvodIdentities = (await this.makeSpecificVozRequest(
        'AUTH',
        requestBody,
        accessToken,
      )) as BVODResponse[];

      return bvodIdentities;
    } catch (err) {
      logger.error('[OzTamClient] cleared saved BVOD access token...', err);

      localStorage.removeItem(BVOD_N10_TOKEN_KEY);

      return [];
    }
  };

  private generateEvent = async (
    eventType: EventType,
    fromPosition: number,
    toPosition: number,
  ): Promise<void> => {
    if (this.stopRecordingEvents) {
      logger.error('[OzTamClient] stop recording events...');

      return;
    }

    const currentTimestamp = Math.floor(Date.now() / 1000);
    const eventTimestamp = new Date();
    const event: Event = {
      timestamp: eventTimestamp.toISOString(),
      event: eventType,
    };

    const isLoad = EventType.LOAD === eventType;
    const isBegin = EventType.BEGIN === eventType;

    let boundedPosition = Math.max(0, toPosition);
    const estimatedToPosition = fromPosition + 60;

    if (
      eventType !== EventType.PROGRESS ||
      Math.abs(boundedPosition - estimatedToPosition) > 1
    ) {
      boundedPosition = Math.min(estimatedToPosition, boundedPosition);
    }

    event.fromPosition = roundPosition(fromPosition);
    event.toPosition = roundPosition(boundedPosition);
    if (this.isLive) {
      const positionDifference = event.toPosition - event.fromPosition;
      if (this.ptsEpochDifference) {
        if (isBegin) {
          /*
            Directly modify ptsEpoch when initial programDateTime
            is behind last playback session's last PROGRESS event
          */
          this.ptsEpochDifference += this.timeFromLastLivePosition;
        }

        event.toPosition = roundPosition(
          event.toPosition + this.ptsEpochDifference,
        );

        if (isLoad) {
          this.timeFromLastReset = inValidRange(
            currentTimestamp - this.resetTimestamp,
          );

          this.timeFromLastLivePosition = inValidRange(
            this.lastLiveToPosition - event.toPosition,
          );

          const resetDiff = this.timeFromLastLivePosition
            ? this.timeFromLastReset
            : 0;

          event.toPosition += this.timeFromLastLivePosition + resetDiff;

          this.timeFromLastLivePosition += resetDiff;
        }

        event.timestamp = new Date(event.toPosition * 1000).toISOString();
      } else {
        // use epoch time if pts epoch difference isn't able to be set
        event.toPosition = roundPosition(eventTimestamp.getTime() / 1000);
      }
      event.fromPosition = event.toPosition - positionDifference;

      if (isLoad || isBegin) {
        event.toPosition = 0;
        event.fromPosition = 0;
      } else {
        this.lastLiveToPosition = event.toPosition;
        this.timeFromLastLivePosition = 0;
      }
    }

    if (
      eventType === EventType.PROGRESS &&
      event.toPosition - event.fromPosition < 0
    ) {
      logger.debug('[OzTamClient] skip oztam negative PROGRESS event');

      return;
    }

    logger.info(
      `[${event.timestamp?.split('T')[1] ||
        event.timestamp}] oztam generate event`,
      event.event,
      event.fromPosition,
      event.toPosition,
      event.toPosition - event.fromPosition,
    );

    await this.dispatchMeterEvent(this.generateMeterEvent([event]));
  };

  private generateMeterEvent = (events: Event[]): MeterEvent | null => {
    if (!this.videoData) {
      logger.error('video data required for ozTeam event');
      return null;
    }

    const eventsWithProperties = [EventType.LOAD, EventType.BEGIN];

    const {
      publisherId,
      mediaId,
      mediaDuration,
      mediaType,
      ...properties
    } = this.videoData;

    // properties needed only for begin event
    const appendProperties = events.some((event) =>
      eventsWithProperties.includes(event.event),
    );

    const meterEvent: MeterEvent = {
      protocolVersion: '1.1.1',
      vendorVersion: this.vendorVersion,
      plugin: 'native',
      ipAddress: this.ipAddress,
      timestamp: new Date().toISOString(),
      timezoneOffset: this.timezoneOffset,
      sessionId: this.sessionId,
      userAgent: {
        deviceType: 'tv',
        os: getOS(),
      },
      publisherId,
      mediaId,
      mediaDuration,
      mediaType,
      events,
    };

    if (appendProperties) {
      const { OzTamId } = config.$get().user || {};
      const currentState = PageNavigation?.getCurrentState() as
          (StandardCarouselItem & LiveCarouselItem) | undefined;
      const genre = currentState?.genre ?? undefined;
      if (properties?.genre === 'none-found' || !properties?.genre) {
        properties.genre = genre;
      }
      meterEvent.properties = {
        connectionType: this.connectionType,
        deviceId: this.deviceId,
        demo1: OzTamId,
        ...properties,
      };
    }

    return meterEvent;
  };

  private getNielsonSubdomainEndpoint = (): string =>
    `https://${this.sessionId}.truesession.com.au`;

  private dispatchMeterEvent = (
    meterEvent: MeterEvent | null,
  ): Promise<void | ParsedResponse<XMLHttpRequest>> => {
    if (!meterEvent) return Promise.resolve();

    const options: Partial<PromiseFetchSettings> = {
      method: 'POST',
      responseType: 'json',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      },
      data: JSON.stringify(meterEvent),
    };

    if (meterEvent.events?.[0]?.event === EventType.LOAD) {
      promiseFetch(this.getNielsonSubdomainEndpoint(), {
        method: 'GET',
      }).catch((error) => {
        logger.error(
          'ozTam client: error sending nielson subdomain request',
          error,
        );
      });
    }

    return promiseFetch(ozTamUrl, options).catch((error) => {
      logger.error('ozTam client: error', error);
      sendErrorToTealium({
        errorCode: TealiumErrorCode.OztamPostRequest,
        errorDetail: JSON.stringify(error),
        errorMessage: error.message,
        errorSource: ozTamUrl,
        errorContext: TealiumErrorContext.API,
      });
    });
  };
}
